import has from 'lodash/has';
import trimEnd from 'lodash/trimEnd';
import Decimal from 'decimal.js';

import appSettings from '../config/settings';
import { types as sdkTypes } from '../util/sdkLoader';

const { subUnitDivisors } = appSettings;
const { Money } = sdkTypes;

// https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Number/MAX_SAFE_INTEGER
// https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Number/MIN_SAFE_INTEGER
// https://stackoverflow.com/questions/26380364/why-is-number-max-safe-integer-9-007-199-254-740-991-and-not-9-007-199-254-740-9
export const MIN_SAFE_INTEGER = Number.MIN_SAFE_INTEGER || -1 * (2 ** 53 - 1);
export const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER || 2 ** 53 - 1;

export const isSafeNumber = decimalValue => {
  if (!(decimalValue instanceof Decimal)) {
    throw new Error('Value must be a Decimal');
  }
  return decimalValue.gte(MIN_SAFE_INTEGER) && decimalValue.lte(MAX_SAFE_INTEGER);
};

// Get the minor unit divisor for the given currency
export const unitDivisor = currency => {
  if (!has(subUnitDivisors, currency)) {
    throw new Error(
      `No minor unit divisor defined for currency: ${currency} in currencySettings.js`
    );
  }
  return subUnitDivisors[currency];
};

////////// Currency manipulation in string format //////////

/**
 * Ensures that the given string uses only dots or commas
 * e.g. ensureSeparator('9999999,99', false) // => '9999999.99'
 *
 * @param {String} str - string to be formatted
 *
 * @return {String} converted string
 */
export const ensureSeparator = (str, useComma = false) => {
  if (typeof str !== 'string') {
    throw new TypeError('Parameter must be a string');
  }
  return useComma ? str.replace(/\./g, ',') : str.replace(/,/g, '.');
};

/**
 * Ensures that the given string uses only dots
 * (e.g. JavaScript floats use dots)
 *
 * @param {String} str - string to be formatted
 *
 * @return {String} converted string
 */
export const ensureDotSeparator = str => {
  return ensureSeparator(str, false);
};

/**
 * Convert string to Decimal object (from Decimal.js math library)
 * Handles both dots and commas as decimal separators
 *
 * @param {String} str - string to be converted
 *
 * @return {Decimal} numeral value
 */
export const convertToDecimal = str => {
  const dotFormattedStr = ensureDotSeparator(str);
  return new Decimal(dotFormattedStr);
};

/**
 * Converts Decimal value to a string (from Decimal.js math library)
 *
 * @param {Decimal|Number|String} decimalValue
 *
 * @param {boolean} useComma - optional.
 * Specify if return value should use comma as separator
 *
 * @return {String} converted value
 */
export const convertDecimalToString = (decimalValue, useComma = false) => {
  const d = new Decimal(decimalValue);
  return ensureSeparator(d.toString(), useComma);
};

// Divisor can be positive value given as Decimal, Number, or String
const convertDivisorToDecimal = divisor => {
  try {
    const divisorAsDecimal = new Decimal(divisor);
    if (divisorAsDecimal.isNegative()) {
      throw new Error(`Parameter (${divisor}) must be a positive number.`);
    }
    return divisorAsDecimal;
  } catch (e) {
    throw new Error(`Parameter (${divisor}) must present a number.`, e);
  }
};

/**
 * Limits value to sub-unit precision: "1.4567" -> "1.45"
 * Useful in input fields so this doesn't use rounding.
 *
 * @param {String} inputString - positive number presentation.
 *
 * @param {Decimal|Number|String} subUnitDivisor - should be something that can be converted to
 * Decimal. (This is a ratio between currency's main unit and sub units.)
 *
 * @param {boolean} useComma - optional.
 * Specify if return value should use comma as separator
 *
 * @return {String} truncated value
 */
export const truncateToSubUnitPrecision = (inputString, subUnitDivisor, useComma = false) => {
  const subUnitDivisorAsDecimal = convertDivisorToDecimal(subUnitDivisor);

  // '10,' should be passed through, but that format is not supported as valid number
  const trimmed = trimEnd(inputString, useComma ? ',' : '.');
  // create another instance and check if value is convertable
  const value = convertToDecimal(trimmed, useComma);

  if (value.isNegative()) {
    throw new Error(`Parameter (${inputString}) must be a positive number.`);
  }

  // Amount is always counted in subunits
  // E.g. $10 => 1000¢
  const amount = value.times(subUnitDivisorAsDecimal);

  if (!isSafeNumber(amount)) {
    throw new Error(
      `Cannot represent money minor unit value ${amount.toString()} safely as a number`
    );
  }

  // Amount must be integer
  // We don't deal with subunit fragments like 1000.345¢
  if (amount.isInteger()) {
    // accepted strings: '9', '9,' '9.' '9,99'
    const decimalCount2 = value.toFixed(2);
    const decimalPrecisionMax2 =
      decimalCount2.length >= inputString.length ? inputString : value.toFixed(2);
    return ensureSeparator(decimalPrecisionMax2, useComma);
  } else {
    // truncate strings ('9.999' => '9.99')
    const truncated = amount.truncated().dividedBy(subUnitDivisorAsDecimal);
    return convertDecimalToString(truncated, useComma);
  }
};

////////// Currency - Money helpers //////////

/**
 * Converts given value to sub unit value and returns it as a number
 *
 * @param {Number|String} value
 *
 * @param {Decimal|Number|String} subUnitDivisor - should be something that can be converted to
 * Decimal. (This is a ratio between currency's main unit and sub units.)
 *
 * @param {boolean} useComma - optional.
 * Specify if return value should use comma as separator
 *
 * @return {number} converted value
 */
export const convertUnitToSubUnit = (value, subUnitDivisor, useComma = false) => {
  const subUnitDivisorAsDecimal = convertDivisorToDecimal(subUnitDivisor);

  if (!(typeof value === 'string' || typeof value === 'number')) {
    throw new TypeError('Value must be either number or string');
  }

  const val = typeof value === 'string' ? convertToDecimal(value, useComma) : new Decimal(value);
  const amount = val.times(subUnitDivisorAsDecimal);

  if (!isSafeNumber(amount)) {
    throw new Error(
      `Cannot represent money minor unit value ${amount.toString()} safely as a number`
    );
  } else if (amount.isInteger()) {
    return amount.toNumber();
  } else {
    throw new Error(`value must divisible by ${subUnitDivisor}`);
  }
};

const isNumber = value => {
  return typeof value === 'number' && !isNaN(value);
};

/* eslint-disable no-underscore-dangle */
// Detect if the given value is a goog.math.Long object
// See: https://google.github.io/closure-library/api/goog.math.Long.html
const isGoogleMathLong = value => {
  return typeof value === 'object' && isNumber(value.low_) && isNumber(value.high_);
};
/* eslint-enable no-underscore-dangle */

/**
 * Convert Money to a number
 *
 * @param {Money} value
 *
 * @return {Number} converted value
 */
export const convertMoneyToNumber = value => {
  if (!(value instanceof Money)) {
    throw new Error('Value must be a Money type');
  }
  const subUnitDivisorAsDecimal = convertDivisorToDecimal(unitDivisor(value.currency));
  let amount;

  if (isGoogleMathLong(value.amount)) {
    // TODO: temporarily also handle goog.math.Long values created by
    // the Transit tooling in the Sharetribe JS SDK. This should be
    // removed when the value.amount will be a proper Decimal type.

    // eslint-disable-next-line no-console
    console.warn('goog.math.Long value in money amount:', value.amount, value.amount.toString());

    amount = new Decimal(value.amount.toString());
  } else {
    amount = new Decimal(value.amount);
  }

  if (!isSafeNumber(amount)) {
    throw new Error(
      `Cannot represent money minor unit value ${amount.toString()} safely as a number`
    );
  }

  return amount.dividedBy(subUnitDivisorAsDecimal).toNumber();
};

function roundToFiveOrTen(number) {
  return Math.ceil(number / 5) * 5
}

/**
 * 
 * @param {*} n 
 * @returns rounded decimals
 */

export function roundDecimal(n) {
  if (!n || isNaN(n)) return null;

  const decimal = roundToFiveOrTen(n.toString().split('.')[1] || 0);
  const int = n.toString().split('.')[0];
  return Number(`${Math.floor(int)}.${decimal}`)
}

// `currencyDisplay: 'narrowSymbol'` is an ECMA-402 option value only recognized since Safari
// 14.1 — on Safari 11.1/12.1, `Intl.NumberFormat` throws a `RangeError` for it at construction
// time (an unrecognized enum value, not a warning), which left prices rendering with no
// currency symbol at all. Feature-detected once and cached, falling back to 'symbol' — the
// original ECMA-402 currencyDisplay value, supported everywhere — when 'narrowSymbol' isn't.
let narrowSymbolSupported = null;
export const isNarrowSymbolSupported = () => {
  if (narrowSymbolSupported === null) {
    try {
      // eslint-disable-next-line no-new
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'narrowSymbol',
      });
      narrowSymbolSupported = true;
    } catch (e) {
      narrowSymbolSupported = false;
    }
  }
  return narrowSymbolSupported;
};

// On browsers without `narrowSymbol` (Safari < 14.1) we format currency with
// `currencyDisplay: 'symbol'`, which prefixes the symbol with the locale currency code for the
// dollar/yen families — CAD -> "CA$", AUD -> "A$", USD -> "US$", NZD -> "NZ$", HK$/MX$/S$,
// CNY -> "CN¥". The app's rule (and the `narrowSymbol` output shown on modern browsers) is the
// bare symbol, e.g. Canadian dollars must render as "$", not "CA$". Every currency this app
// supports (see settingsCurrency.js) whose symbol is $/¥ narrows to just $/¥ — none need the
// prefix kept (there is no e.g. BRL "R$") — so stripping the uppercase-letter prefix immediately
// before a $/¥ reproduces `narrowSymbol` everywhere. No-op on the modern `narrowSymbol` path and
// on symbols that have no such prefix (€, £, ₹, kr, …).
export const stripCurrencyCodePrefix = formatted =>
  typeof formatted === 'string' ? formatted.replace(/[A-Z]{1,4}(?=[$¥])/, '') : formatted;

/**
 * Format the given money to a string
 *
 * @param {Object} intl
 * @param {Money} value
 *
 * @return {String} formatted money value
 */
export const formatMoney = (intl, value) => {
  if (!(value instanceof Money)) {
    throw new Error('Value must be a Money type');
  }

  const valueAsNumber = convertMoneyToNumber(value);


  // See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
  const numberFormatOptions = {
    style: 'currency',
    currency: value.currency,
    currencyDisplay: isNarrowSymbolSupported() ? 'narrowSymbol' : 'symbol',
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const val = roundDecimal(Number(valueAsNumber.toFixed(2)));
  const v = val < 1 ? Math.ceil(val) : val;
  const formattedValue = intl.formatNumber(valueAsNumber, numberFormatOptions)
  // Strip the locale currency-code prefix (e.g. "CA$" -> "$", "US$" -> "$") so the symbol
  // matches `narrowSymbol` on browsers that fall back to `currencyDisplay: 'symbol'`.
  return stripCurrencyCodePrefix(formattedValue);
};

/**
 * Format the given major-unit string value as currency. E.g. "10" -> "$10".
 *
 * NOTE: This function should not be used with listing prices or other Money type.
 * This can be used with price filters and other components that doesn't send Money types to API.
 *
 * @param {Object} intl
 * @param {String} value
 *
 * @return {String} formatted money value
 */
export const formatCurrencyMajorUnit = (intl, currency, valueWithoutSubunits) => {
  const valueAsNumber = new Decimal(valueWithoutSubunits).toNumber();

  // See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
  const numberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  // `currencyDisplay: 'symbol'` prefixes the dollar/yen families with their locale code
  // (CAD -> "CA$"); strip it so Canadian etc. render with the bare "$" per the app's rule.
  return stripCurrencyCodePrefix(intl.formatNumber(valueAsNumber, numberFormatOptions));
};

/**
 * A function that converts amount in the given currency.
 * @param {*} exchangeRates 
 * @param {*} amount 
 * @param {*} fromCurrency 
 * @param {*} toCurrency 
 * @returns {object} new money object with the converted amount and currency.
 */
export function convertPrice(exchangeRates, amount, fromCurrency, toCurrency) {

  if (fromCurrency === toCurrency) {
    return new Money(amount, fromCurrency)
  }
  const conversionRate = findExchangeRate(exchangeRates, fromCurrency, toCurrency);
  if (conversionRate) {
    const fromDivisor = subUnitDivisors[fromCurrency];
    const toDivisor = subUnitDivisors[toCurrency];

    // Convert from subunits to major units for the source currency
    const amountInMajorUnits = fromDivisor === 1 ? (amount / 100) : amount / fromDivisor;

    // Apply conversion rate
    const convertedInMajorUnits = amountInMajorUnits * conversionRate;

    // Convert back to subunits for the target currency
    const convertedInSubUnits = toDivisor === 1
      ? Math.round(convertedInMajorUnits * 100)
      : Math.round(convertedInMajorUnits * toDivisor);

    const updatedPrice = new Money(convertedInSubUnits, toCurrency);
    return updatedPrice;
  } else {
    return new Money(amount, fromCurrency);
  }
}

/**
 * Helper function to find exchange rate between the given currencies.
 * @param {*} exchangeRates 
 * @param {*} fromCurrency 
 * @param {*} toCurrency 
 * @returns {number} exchange rate
 */
export function findExchangeRate(exchangeRates = [], fromCurrency, toCurrency) {
  if (fromCurrency === toCurrency) return 1;
  const rate = exchangeRates.find(rate =>
    rate.from_currency === fromCurrency && rate.to_currency === toCurrency
  );

  return rate ? rate.exchange_rate : null;
}

/**
 * A function that converts line items to a given currency.
 * @param {*} exchangeRates 
 * @param {*} lineItems 
 * @param {*} toCurrency 
 * @returns {object} updated line items with the given currency.
 */

export const convertLineItems = (exchangeRates, lineItems, toCurrency = 'USD') => {
  return !!lineItems && lineItems.length ? lineItems.map(item => {

    const { lineTotal, unitPrice } = item;
    // A multi-day package's unit price is the package total divided by its day count
    // (server calculateNewUnitPriceFromTotalPrice), so it arrives as a FRACTIONAL
    // subunit amount - e.g. $57.50 over 3 days = 1916.6667. Converting that fraction
    // straight to the display currency gave $26.16, while the checkout breakdown -
    // which patches in transaction metadata and rounds to whole subunits first
    // (OrderBreakdown.patchLineItemsWithMetadata) - gave $26.17 for the same booking.
    // Round half-up to whole subunits here so both paths agree. Display only: totals
    // are summed from lineTotal, never from unitPrice x quantity, and lineTotal is
    // deliberately left untouched so no total can shift.
    const roundedUnitAmount = Math.round(unitPrice.amount);
    const updateUnitPrice = convertPrice(exchangeRates, roundedUnitAmount, unitPrice.currency, toCurrency);
    const updateLineTotal = convertPrice(exchangeRates, lineTotal.amount, lineTotal.currency, toCurrency);
    return {
      ...item,
      unitPrice: updateUnitPrice,
      lineTotal: updateLineTotal,
    }
  }) : [];
};

export const isSupportedCurrency = (supportedCurrencies, currency) => {
  return supportedCurrencies.includes(currency);
}

export const convertMoneyToDecimalJS = (money) => {
  if (!(money instanceof Money)) {
    throw new Error('Value must be a Money type');
  }
  return new Decimal(money.amount);
};

export const decimalJSToNumber = (decimal) => {
  if (!(decimal instanceof Decimal)) {
    throw new Error('Value must be a Decimal type');
  }
  return decimal.toNumber();
};
